import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceNamespaceEntity } from '@unifyapps/defs/types/namespace';
import {
  useSearchDeployedEntities,
  useSearchDeployedNamespaces,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useGetUserContext } from '../../../../useGetUserContext';
import type { FetchInterfaceNamespacesParams } from '../../../standardEntities/data/useFetchInterfaceNamespaces/types';
import {
  getInterfaceComponentNamespaceQueryFilters,
  getShouldFetchNamespace,
} from '../../../standardEntities/data/useFetchInterfaceNamespaces/utils';
import { getDeployedInterfaceCommonNamespaceSearchRequest } from './utils';

type Namespace = InterfaceNamespaceEntity | undefined;

export const useFetchDeployedInterfacePageNamespace = (
  props: Omit<FetchInterfaceNamespacesParams, 'locale'>,
) => {
  const { userContext } = useGetUserContext();
  const { interfaceId, interfaceRecord, interfacePageId, mode } = props;

  const userLocale = userContext?.locale;
  const locale = InterfaceHelper.getLocale({
    userLocale,
    interfaceRecord,
    mode,
  });
  const shouldFetchNamespace = getShouldFetchNamespace(locale, interfaceRecord);

  const {
    data: pageNamespaceData,
    isLoading,
    error,
  } = useSearchDeployedEntities(
    UAEntityTypes.InterfaceNamespace,
    getInterfaceComponentNamespaceQueryFilters({
      interfaceId,
      interfaceComponentId: interfacePageId ?? '',
      locale,
    }),
    {
      query: {
        enabled: Boolean(interfacePageId && shouldFetchNamespace),
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  return {
    isLoading,
    error,
    interfacePageNamespace: pageNamespaceData?.objects?.[0] as Namespace,
  };
};

export const useFetchDeployedInterfaceCommonNamespace = (
  props: Omit<FetchInterfaceNamespacesParams, 'interfacePageId' | 'locale'>,
) => {
  const { userContext } = useGetUserContext();
  const { interfaceId, interfaceRecord, mode } = props;

  const userLocale = userContext?.locale;
  const locale = InterfaceHelper.getLocale({
    userLocale,
    interfaceRecord,
    mode,
  });
  const shouldFetchNamespace = getShouldFetchNamespace(locale, interfaceRecord);

  const {
    data: commonNamespaceData,
    isLoading,
    error,
  } = useSearchDeployedNamespaces(
    getDeployedInterfaceCommonNamespaceSearchRequest({
      interfaceId,
      locale,
    }),
    {
      query: {
        enabled: shouldFetchNamespace,
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  return {
    isLoading,
    error,
    commonNamespace: commonNamespaceData?.objects?.[0] as Namespace,
  };
};
